import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowUp, FaExclamationCircle, FaEnvelope, FaInfoCircle, FaCheck } from 'react-icons/fa';
import './LegalPages.css';

const TermsOfService = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Handle scroll for back to top button
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  // Sections for Table of Contents
  const sections = [
    { id: 'introduction', title: 'Introduction' },
    { id: 'account-registration', title: 'Account Registration' },
    { id: 'use-of-services', title: 'Use of Services' },
    { id: 'user-content', title: 'User Content' },
    { id: 'privacy', title: 'Privacy' },
    { id: 'subscription-payment', title: 'Subscription and Payment' },
    { id: 'termination', title: 'Termination' },
    { id: 'disclaimer', title: 'Disclaimer of Warranties' },
    { id: 'limitation', title: 'Limitation of Liability' },
    { id: 'changes-to-terms', title: 'Changes to Terms' },
    { id: 'contact', title: 'Contact Us' }
  ];

  return (
    <div className="legal-page">
      {/* SVG Definitions for Patterns */}
      <svg 
        width="0" 
        height="0" 
        style={{ position: 'absolute' }}
      >
        <defs>
          <pattern 
            id="gridPattern" 
            width="20" 
            height="20" 
            patternUnits="userSpaceOnUse"
          >
            <path 
              d="M 20 0 L 0 0 0 20" 
              fill="none" 
              stroke="white" 
              strokeWidth="0.5" 
              opacity="0.3"
            />
          </pattern>
        </defs>
      </svg>

      <div className="legal-hero">
        <div className="legal-hero__overlay">
          <svg 
            width="100%" 
            height="100%" 
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect 
              width="100%" 
              height="100%" 
              fill="url(#gridPattern)" 
            />
          </svg>
        </div>
        <div className="legal-container">
          <h1 className="legal-hero__title">Terms of Service</h1>
          <p className="legal-hero__subtitle">
            Please review these terms carefully before using CVance & OppLiy services
          </p>
          <span className="legal-last-updated">
            Last updated: May 4, 2025
          </span>
        </div>
      </div>

      <div className="legal-container">
        <div className="legal-content">
          <div className="legal-toc">
            <h2 className="legal-toc__title">Table of Contents</h2>
            <ul className="legal-toc__list">
              {sections.map(section => (
                <li key={section.id} className="legal-toc__item">
                  <a 
                    href={`#${section.id}`} 
                    className="legal-toc__link"
                  >
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <section id="introduction" className="legal-section">
            <h2 className="legal-section__title">1. Introduction</h2>
            <p>
              Welcome to CVance & OppLiy ("we," "our," or "us"). By accessing or using our website, mobile application, or any of our services (collectively, the "Services"), you agree to be bound by these Terms of Service ("Terms").
            </p>
            <p>
              Please read these Terms carefully. They contain important information about your legal rights and obligations. If you do not agree with these Terms, please do not use our Services.
            </p>
            <div className="legal-highlight legal-highlight--info">
              <p>
                <FaInfoCircle style={{ marginRight: '10px' }} />
                CVance and OppLiy are currently in the project development phase. We are not a registered company or LLC at this time. This is a non-profit project aimed at addressing challenges in the job search process.
              </p>
            </div>
          </section>

          <section id="account-registration" className="legal-section">
            <h2 className="legal-section__title">2. Account Registration</h2>
            <p>
              To access certain features of our Services, you may need to register for an account. When you register, you agree to provide accurate, current, and complete information about yourself.
            </p>
            <p>
              You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
            </p>
            <p>
              We reserve the right to suspend or terminate your account if we determine, in our sole discretion, that you have violated these Terms or engaged in any fraudulent, abusive, or unlawful activity.
            </p>
          </section>

          <section id="use-of-services" className="legal-section">
            <h2 className="legal-section__title">3. Use of Services</h2>
            <p>
              Our Services are designed to help you create and tailor resumes for job applications and find relevant job opportunities. You agree to use our Services only for lawful purposes and in accordance with these Terms.
            </p>
            <p>
              You agree not to:
            </p>
            <ul>
              <li>Use our Services in any way that violates any applicable law or regulation</li>
              <li>Infringe upon the rights of others, including intellectual property rights</li>
              <li>Attempt to gain unauthorized access to our systems or interfere with the operation of our Services</li>
              <li>Use our Services to distribute malware, spyware, or other harmful software</li>
              <li>Engage in any activity that could disable, overburden, or impair our Services</li>
              <li>Create false or misleading information in your resume or profile</li>
            </ul>
          </section>

          <section id="user-content" className="legal-section">
            <h2 className="legal-section__title">4. User Content</h2>
            <p>
              You retain ownership of any content you submit to our Services, including your resume, profile information, and other materials (collectively, "User Content").
            </p>
            <p>
              By submitting User Content, you grant us a non-exclusive, worldwide, royalty-free license to use, copy, modify, and display your User Content solely for the purpose of providing and improving our Services.
            </p>
            <p>
              You are solely responsible for your User Content and the consequences of sharing it. You represent and warrant that:
            </p>
            <ul>
              <li>You own or have the necessary rights to your User Content</li>
              <li>Your User Content does not violate the rights of any third party</li>
              <li>Your User Content does not contain any false, misleading, or fraudulent information</li>
            </ul>
          </section>

          <section id="privacy" className="legal-section">
            <h2 className="legal-section__title">5. Privacy</h2>
            <p>
              Our <Link to="/privacy" style={{ color: '#4a6bfd', fontWeight: '600' }}>Privacy Policy</Link>, which is incorporated into these Terms by reference, describes how we collect, use, and share your personal information. By using our Services, you consent to the data practices described in our Privacy Policy.
            </p>
            <div className="legal-highlight">
              <p>
                <FaCheck style={{ marginRight: '10px', color: '#4a6bfd' }} />
                We take data protection seriously and implement appropriate security measures to protect your information.
              </p>
            </div>
          </section>

          <section id="subscription-payment" className="legal-section">
            <h2 className="legal-section__title">6. Subscription and Payment</h2>
            <p>
              We offer free and paid subscription plans. By subscribing to a paid plan, you agree to pay all fees associated with the plan you select.
            </p>
            <p>
              All payments are processed through third-party payment processors. You agree to comply with their terms of service and provide accurate billing information.
            </p>
            <p>
              Subscription fees are billed in advance on a monthly or annual basis, depending on the plan you select. We do not provide refunds for partial subscription periods.
            </p>
            <div className="legal-highlight legal-highlight--info">
              <p>
                <FaInfoCircle style={{ marginRight: '10px' }} />
                During our project development phase, all services are currently offered free of charge. If and when paid subscription plans are implemented, you will be notified in advance.
              </p>
            </div>
          </section>

          <section id="termination" className="legal-section">
            <h2 className="legal-section__title">7. Termination</h2>
            <p>
              You may terminate your account at any time by following the instructions in your account settings.
            </p>
            <p>
              We may terminate or suspend your account and access to our Services at any time, without prior notice or liability, for any reason, including if we believe you have violated these Terms.
            </p>
            <p>
              Upon termination, your right to use our Services will immediately cease. All provisions of these Terms that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
            </p>
          </section>

          <section id="disclaimer" className="legal-section">
            <h2 className="legal-section__title">8. Disclaimer of Warranties</h2>
            <div className="legal-highlight legal-highlight--warning">
              <p>
                <FaExclamationCircle style={{ marginRight: '10px', color: '#f59e0b' }} />
                The following disclaimer outlines important limitations of our Services. Please read carefully.
              </p>
            </div>
            <p>
              OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT ANY WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, OR COURSE OF PERFORMANCE.
            </p>
            <p>
              WE DO NOT WARRANT THAT: (A) OUR SERVICES WILL FUNCTION UNINTERRUPTED, SECURE, OR AVAILABLE AT ANY PARTICULAR TIME OR LOCATION; (B) ANY ERRORS OR DEFECTS WILL BE CORRECTED; (C) OUR SERVICES ARE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS; OR (D) THE RESULTS OF USING OUR SERVICES WILL MEET YOUR REQUIREMENTS.
            </p>
            <p>
              WE DO NOT GUARANTEE THAT USING OUR SERVICES WILL RESULT IN JOB INTERVIEWS OR OFFERS.
            </p>
          </section>

          <section id="limitation" className="legal-section">
            <h2 className="legal-section__title">9. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL WE BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM: (A) YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE OUR SERVICES; (B) ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON OUR SERVICES; (C) ANY CONTENT OBTAINED FROM OUR SERVICES; AND (D) UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT, WHETHER BASED ON WARRANTY, CONTRACT, TORT (INCLUDING NEGLIGENCE), OR ANY OTHER LEGAL THEORY, WHETHER OR NOT WE HAVE BEEN INFORMED OF THE POSSIBILITY OF SUCH DAMAGE.
            </p>
          </section>

          <section id="changes-to-terms" className="legal-section">
            <h2 className="legal-section__title">10. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will provide notice of significant changes by posting the updated Terms on our website and updating the "Last updated" date.
            </p>
            <p>
              Your continued use of our Services after any changes to these Terms constitutes your acceptance of the revised Terms.
            </p>
          </section>

          <section id="contact" className="legal-section">
            <h2 className="legal-section__title">11. Contact Us</h2>
            <p>
              If you have any questions about these Terms, please contact us at:
            </p>
            <p>
              Email: <a href="mailto:oppliycvance@gmail.com" style={{ color: '#4a6bfd', fontWeight: '600' }}>oppliycvance@gmail.com</a>
            </p>
          </section>
        </div>

        <div className="legal-contact">
          <h2 className="legal-contact__title">Still Have Questions?</h2>
          <p className="legal-contact__text">
            If you need further clarification about our Terms of Service or have any concerns, our team is here to help you.
          </p>
          <Link to="/contact" className="legal-contact__button">
            Contact Support
          </Link>
        </div>
      </div>

      {/* Back to top button */}
      <div 
        className={`legal-back-to-top ${showBackToTop ? 'visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Back to top"
      >
        <FaArrowUp />
      </div>
    </div>
  );
};

export default TermsOfService;